/*
 * nco-collection.js
 *
 * Collection page controller — client-side sort, mobile filter overlay
 * open/close with scroll lock, grid card visibility driven by
 * nco:filter:change events from the 4.2 filter engine, and SEO
 * rel-prev/next injection.
 *
 * Standards:
 *   E5  — zero ESLint errors/warnings
 *   E7  — loaded as type="module" defer, never blocks render
 *   S2  — vanilla JS, event delegation, no jQuery
 *   S5  — ARIA state, keyboard (Escape), focus management
 *   S6  — deferred, no render-blocking
 */

/* ------------------------------------------------------------------ */
/* Selectors                                                           */
/* ------------------------------------------------------------------ */

const SEL = {
  collection: '[data-nco-collection]',
  sidebar: '[data-nco-sidebar]',
  filterToggle: '[data-nco-filter-toggle]',
  filterClose: '[data-nco-filter-overlay-close]',
  filterApply: '[data-nco-filter-apply]',
  backdrop: '[data-nco-backdrop]',
  grid: '[data-nco-grid]',
  card: '[data-nco-family-id]',
  sort: '[data-nco-sort]',
  visibleCount: '[data-nco-visible-count]',
  overlayCount: '[data-nco-overlay-count]',
  cta: '[data-nco-cta]',
  prevLink: '.nco-collection__page-link--prev',
  nextLink: '.nco-collection__page-link--next',
  pagination: '[data-nco-pagination]',
  pagePrev: '[data-nco-page-prev]',
  pageNext: '[data-nco-page-next]',
  pageInfo: '[data-nco-page-info]',
};

const CSS_OPEN = 'nco-collection__sidebar--open';
const CSS_BACKDROP = 'nco-collection__backdrop--visible';

/* ------------------------------------------------------------------ */
/* State                                                               */
/* ------------------------------------------------------------------ */

let currentSort = 'featured';
let currentPage = 1;
let perPage = 24;
let visibleFamilyIds = [];

/* ------------------------------------------------------------------ */
/* Mobile filter overlay                                               */
/* ------------------------------------------------------------------ */

function openOverlay() {
  const sidebar = document.querySelector(SEL.sidebar);
  const backdrop = document.querySelector(SEL.backdrop);
  if (!sidebar) return;

  sidebar.classList.add(CSS_OPEN);
  if (backdrop) backdrop.classList.add(CSS_BACKDROP);
  document.body.style.overflow = 'hidden';

  const closeBtn = sidebar.querySelector(SEL.filterClose);
  if (closeBtn) closeBtn.focus();
}

function closeOverlay() {
  const sidebar = document.querySelector(SEL.sidebar);
  const backdrop = document.querySelector(SEL.backdrop);
  if (!sidebar) return;

  sidebar.classList.remove(CSS_OPEN);
  if (backdrop) backdrop.classList.remove(CSS_BACKDROP);
  document.body.style.overflow = '';

  const toggle = document.querySelector(SEL.filterToggle);
  if (toggle) toggle.focus();
}

/* ------------------------------------------------------------------ */
/* Sort                                                                */
/* ------------------------------------------------------------------ */

function sortComparator(a, b) {
  switch (currentSort) {
    case 'price-asc':
      return Number(a.dataset.ncoCardPrice) - Number(b.dataset.ncoCardPrice);
    case 'price-desc':
      return Number(b.dataset.ncoCardPrice) - Number(a.dataset.ncoCardPrice);
    case 'newest':
      return Number(b.dataset.ncoCardDate) - Number(a.dataset.ncoCardDate);
    default:
      return Number(a.dataset.ncoCardOrder) - Number(b.dataset.ncoCardOrder);
  }
}

function sortCardArray(cards) {
  cards.sort(sortComparator);
}

function sortCards() {
  const grid = document.querySelector(SEL.grid);
  if (!grid) return;

  const cards = Array.from(grid.querySelectorAll(SEL.card));
  const cta = grid.querySelector(SEL.cta);

  cards.sort(sortComparator);

  if (cta) cta.remove();

  for (let i = 0; i < cards.length; i++) {
    grid.appendChild(cards[i]);
  }

  if (cta) {
    const cols = getComputedStyle(grid).getPropertyValue('grid-template-columns');
    const colCount = cols ? cols.split(' ').length : 3;
    const visibleCards = cards.filter(function (c) { return !c.hidden; });
    const insertAfter = Math.min(colCount, visibleCards.length);
    const refCard = visibleCards[insertAfter - 1];
    if (refCard && refCard.nextSibling) {
      grid.insertBefore(cta, refCard.nextSibling);
    } else {
      grid.appendChild(cta);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Grid visibility from filter events                                  */
/* ------------------------------------------------------------------ */

function updateGridVisibility(familyIds, detail) {
  visibleFamilyIds = familyIds;
  currentPage = 1;

  if (detail) {
    updateCardContent(detail);
  }

  applyPagination();
}

/* ------------------------------------------------------------------ */
/* Card content updates from filter resolution                         */
/* ------------------------------------------------------------------ */

function formatCardPrice(cents) {
  return '$' + (cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function updateCardContent(detail) {
  var resolved = detail.resolved || {};
  var filters = detail.activeFilters || {};
  var products = detail.products || [];
  var hasActiveFilters = Object.keys(filters).some(function (k) {
    return filters[k] && filters[k].length > 0;
  });

  var singleColour = (filters.colour && filters.colour.length === 1) ? filters.colour[0] : null;
  var singleColourHandle = singleColour
    ? singleColour.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    : null;

  var productMap = {};
  for (var p = 0; p < products.length; p++) {
    productMap[products[p].id] = products[p];
  }

  var cards = document.querySelectorAll('[data-nco-product-card]');
  for (var i = 0; i < cards.length; i++) {
    var card = cards[i];
    var wrapper = card.closest('[data-nco-family-id]');
    var familyId = wrapper ? wrapper.getAttribute('data-nco-family-id') : '';
    var cardProductId = card.getAttribute('data-product-id');
    var productId = familyId || cardProductId;
    var handle = card.getAttribute('data-nco-handle');
    var defaultUrl = card.getAttribute('data-nco-default-url') || ('/products/' + handle);
    var defaultPrice = Number(card.getAttribute('data-nco-default-price') || 0);

    var links = card.querySelectorAll('[data-nco-card-link]');
    var priceEl = card.querySelector('[data-nco-card-price-text]');
    var imgEl = card.querySelector('[data-nco-card-image]');

    var res = resolved[productId] || resolved[cardProductId];
    var productIndexEntry = productMap[productId] || productMap[cardProductId];

    if (res && hasActiveFilters) {
      // Update price
      if (priceEl) {
        var priceText = res.isRange
          ? 'From ' + formatCardPrice(res.price)
          : formatCardPrice(res.price);
        priceEl.textContent = priceText;
      }

      // Update card sort attribute on wrapper (cents)
      if (wrapper) {
        wrapper.setAttribute('data-nco-card-price', String(res.price));
      }

      // Update links
      for (var l = 0; l < links.length; l++) {
        links[l].href = res.url;
      }
    } else {
      // Revert to defaults
      if (priceEl && defaultPrice) {
        priceEl.textContent = formatCardPrice(defaultPrice);
      }
      for (var l2 = 0; l2 < links.length; l2++) {
        links[l2].href = defaultUrl;
      }
      if (wrapper && defaultPrice) {
        wrapper.setAttribute('data-nco-card-price', String(defaultPrice));
      }
    }

    if (imgEl) {
      var defaultImg = imgEl.getAttribute('data-nco-default-image');
      var defaultAlt = imgEl.getAttribute('data-nco-default-alt');

      if (singleColourHandle) {
        var didSwap = false;
        var cim = productIndexEntry ? productIndexEntry.colourImageMap : null;
        if (cim && cim[singleColourHandle] && cim[singleColourHandle].src) {
          imgEl.src = cim[singleColourHandle].src;
          imgEl.removeAttribute('srcset');
          if (cim[singleColourHandle].alt) imgEl.alt = cim[singleColourHandle].alt;
          imgEl.setAttribute('data-nco-image-swapped', '1');
          didSwap = true;
        }
        if (!didSwap && imgEl.hasAttribute('data-nco-image-swapped') && defaultImg) {
          imgEl.src = defaultImg;
          imgEl.removeAttribute('srcset');
          if (defaultAlt) imgEl.alt = defaultAlt;
          imgEl.removeAttribute('data-nco-image-swapped');
        }
      } else if (imgEl.hasAttribute('data-nco-image-swapped') && defaultImg) {
        imgEl.src = defaultImg;
        imgEl.removeAttribute('srcset');
        if (defaultAlt) imgEl.alt = defaultAlt;
        imgEl.removeAttribute('data-nco-image-swapped');
      }
    }
  }
}

/* ------------------------------------------------------------------ */
/* Client-side pagination                                              */
/* ------------------------------------------------------------------ */

function applyPagination() {
  const cards = document.querySelectorAll(SEL.card);
  const totalVisible = visibleFamilyIds.length;
  const totalPages = Math.max(1, Math.ceil(totalVisible / perPage));

  if (currentPage > totalPages) currentPage = totalPages;

  /* Build an ordered list of visible cards (respecting current sort) */
  const sortedCards = Array.from(cards).filter(function (c) {
    return visibleFamilyIds.indexOf(c.getAttribute('data-nco-family-id')) !== -1;
  });

  sortCardArray(sortedCards);

  /* Determine which cards belong to the current page */
  const startIdx = (currentPage - 1) * perPage;
  const endIdx = startIdx + perPage;
  const pageIds = {};
  for (let i = startIdx; i < Math.min(endIdx, sortedCards.length); i++) {
    pageIds[sortedCards[i].getAttribute('data-nco-family-id')] = true;
  }

  /* Show/hide cards */
  for (let i = 0; i < cards.length; i++) {
    const fid = cards[i].getAttribute('data-nco-family-id');
    const show = !!pageIds[fid];
    cards[i].hidden = !show;
  }

  /* Update counts */
  const countStr = String(totalVisible);
  const countEls = document.querySelectorAll(SEL.visibleCount);
  for (let j = 0; j < countEls.length; j++) countEls[j].textContent = countStr;
  const overlayEls = document.querySelectorAll(SEL.overlayCount);
  for (let k = 0; k < overlayEls.length; k++) overlayEls[k].textContent = countStr;

  /* Update pagination controls */
  const prevBtn = document.querySelector(SEL.pagePrev);
  const nextBtn = document.querySelector(SEL.pageNext);
  const pageInfoEl = document.querySelector(SEL.pageInfo);
  const paginationNav = document.querySelector(SEL.pagination);

  if (paginationNav) {
    paginationNav.hidden = totalPages <= 1;
  }
  if (prevBtn) prevBtn.disabled = currentPage <= 1;
  if (nextBtn) nextBtn.disabled = currentPage >= totalPages;
  if (pageInfoEl) {
    pageInfoEl.textContent = 'Page ' + currentPage + ' of ' + totalPages;
  }

  sortCards();
}

/* ------------------------------------------------------------------ */
/* SEO: inject rel prev / next into <head>                             */
/* ------------------------------------------------------------------ */

function injectPaginationLinks() {
  const prev = document.querySelector(SEL.prevLink);
  const next = document.querySelector(SEL.nextLink);

  if (prev) {
    const link = document.createElement('link');
    link.rel = 'prev';
    link.href = prev.href;
    document.head.appendChild(link);
  }

  if (next) {
    const link = document.createElement('link');
    link.rel = 'next';
    link.href = next.href;
    document.head.appendChild(link);
  }
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

function init() {
  const collection = document.querySelector(SEL.collection);
  if (!collection) return;

  perPage = Number(collection.getAttribute('data-nco-per-page')) || 24;

  const filterToggle = collection.querySelector(SEL.filterToggle);
  if (filterToggle) {
    filterToggle.addEventListener('click', openOverlay);
  }

  const filterClose = collection.querySelector(SEL.filterClose);
  if (filterClose) {
    filterClose.addEventListener('click', closeOverlay);
  }

  const filterApply = collection.querySelector(SEL.filterApply);
  if (filterApply) {
    filterApply.addEventListener('click', closeOverlay);
  }

  const backdrop = collection.querySelector(SEL.backdrop);
  if (backdrop) {
    backdrop.addEventListener('click', closeOverlay);
  }

  const sortSelect = collection.querySelector(SEL.sort);
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      currentSort = sortSelect.value;
      currentPage = 1;
      applyPagination();
    });
  }

  const initPrevBtn = collection.querySelector(SEL.pagePrev);
  if (initPrevBtn) {
    initPrevBtn.addEventListener('click', function () {
      if (currentPage > 1) {
        currentPage--;
        applyPagination();
        collection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  const initNextBtn = collection.querySelector(SEL.pageNext);
  if (initNextBtn) {
    initNextBtn.addEventListener('click', function () {
      const totalPages = Math.max(1, Math.ceil(visibleFamilyIds.length / perPage));
      if (currentPage < totalPages) {
        currentPage++;
        applyPagination();
        collection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  document.addEventListener('nco:filter:change', function (e) {
    const detail = e.detail;
    if (detail && detail.visibleFamilyIds) {
      updateGridVisibility(detail.visibleFamilyIds, detail);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      const sidebar = document.querySelector(SEL.sidebar);
      if (sidebar && sidebar.classList.contains(CSS_OPEN)) {
        closeOverlay();
      }
    }
  });

  injectPaginationLinks();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
